import { useRequiredContext } from "@redotech/react-util/context";
import { toast } from "@redotech/redo-web/alert";
import { useMutation } from "@tanstack/react-query";
import { ChangeEvent, useCallback, useState } from "react";
import { RedoAdminClientContext } from "../../../../client/context";
import { uploadZendeskConversations } from "../../../../client/team";

interface UseZendeskConversationsImportOptions {
  teamId: string | undefined;
}

export function useZendeskConversationsImport({
  teamId,
}: UseZendeskConversationsImportOptions) {
  const client = useRequiredContext(RedoAdminClientContext);
  const [file, setFile] = useState<File | null>(null);
  const [merchantEmail, setMerchantEmail] = useState("");

  const mutation = useMutation({
    mutationFn: async (fileToUpload: File) => {
      if (!teamId) {
        throw new Error("Team ID is required");
      }
      return uploadZendeskConversations(client, {
        teamId,
        file: fileToUpload,
        merchantEmail: merchantEmail || undefined,
      });
    },
    onSuccess: (result) => {
      toast(
        `Zendesk conversations imported: ${result.importedCount} imported, ${result.skippedCount} skipped`,
        { variant: "success" },
      );
      setFile(null);
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      toast(`Failed to import Zendesk conversations: ${errorMessage}`, {
        variant: "error",
      });
    },
  });

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files[0]) {
        setFile(files[0]);
      }
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    if (file && teamId) {
      mutation.mutate(file);
    }
  }, [file, teamId, mutation]);

  return {
    file,
    loading: mutation.isPending,
    merchantEmail,
    setMerchantEmail,
    handleFileChange,
    handleSubmit,
  };
}
